import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Video, Code2, BrainCircuit, Users, Briefcase, Building2 } from 'lucide-react';
import InterviewSimulator from '../components/InterviewSimulator';

type InterviewType = 'technical' | 'behavioral' | 'system-design' | 'pair-programming';
type ExperienceLevel = 'junior' | 'mid' | 'senior';
type CompanyType = 'startup' | 'enterprise' | 'faang';

export default function Interviews() {
  const [selectedType, setSelectedType] = useState<InterviewType>('technical');
  const [selectedLevel, setSelectedLevel] = useState<ExperienceLevel>('mid');
  const [selectedCompany, setSelectedCompany] = useState<CompanyType>('enterprise');
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);

  const interviewTypes = [
    {
      id: 'technical',
      title: 'Technical Interview',
      icon: Code2,
      description: 'Practice coding questions, algorithms, and data structures'
    },
    {
      id: 'behavioral',
      title: 'Behavioral Interview',
      icon: Users,
      description: 'Master common behavioral and situational questions'
    },
    {
      id: 'system-design',
      title: 'System Design',
      icon: BrainCircuit,
      description: 'Learn to design scalable software systems'
    },
    {
      id: 'pair-programming',
      title: 'Pair Programming',
      icon: Video,
      description: 'Practice collaborative coding sessions'
    }
  ];

  const companyTypes = [
    {
      id: 'startup',
      title: 'Startup',
      icon: Building2,
      description: 'Fast-paced, full-stack focused interviews'
    },
    {
      id: 'enterprise',
      title: 'Enterprise',
      icon: Building2,
      description: 'Traditional corporate technical interviews'
    },
    {
      id: 'faang',
      title: 'FAANG',
      icon: Building2,
      description: 'Big tech company interview style'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Interview Practice
          </motion.h1>
          <motion.p 
            className="text-xl text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Practice with our AI-powered interview simulator tailored to your experience level and company preferences.
          </motion.p>
        </div>

        {/* Interview Type Selection */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {interviewTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id as InterviewType)}
              className={`p-6 rounded-xl transition-all duration-300 ${
                selectedType === type.id
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <type.icon className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{type.title}</h3>
              <p className="text-sm opacity-80">{type.description}</p>
            </button>
          ))}
        </motion.div>

        {/* Experience Level Selection */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h2 className="text-2xl font-semibold text-white mb-6">Experience Level</h2>
          <div className="flex flex-wrap gap-4">
            {['junior', 'mid', 'senior'].map((level) => (
              <button
                key={level}
                onClick={() => setSelectedLevel(level as ExperienceLevel)}
                className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                  selectedLevel === level
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)} Level
              </button>
            ))}
          </div>
        </motion.div>

        {/* Company Type Selection */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {companyTypes.map((company) => (
            <button
              key={company.id}
              onClick={() => setSelectedCompany(company.id as CompanyType)}
              className={`p-6 rounded-xl transition-all duration-300 ${
                selectedCompany === company.id
                  ? 'bg-green-600 text-white shadow-lg shadow-green-500/20'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <company.icon className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{company.title}</h3>
              <p className="text-sm opacity-80">{company.description}</p>
            </button>
          ))}
        </motion.div>

        {/* Start Interview Button */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <button
            onClick={() => setIsSimulatorOpen(true)}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Start Interview Practice
          </button>
        </motion.div>
      </div>

      {/* Interview Simulator */}
      {isSimulatorOpen && (
        <InterviewSimulator
          interviewType={selectedType}
          experienceLevel={selectedLevel}
          companyType={selectedCompany}
          onClose={() => setIsSimulatorOpen(false)}
        />
      )}
    </div>
  );
} 